import * as React from 'react';
import { getAllSystemUsers } from '@/api/accounts';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Tab, Tabs } from '@mui/material';
import type { AxiosError } from 'axios';

import { useUser } from '@/hooks/v2/use-user';

import { SystemUsersTable } from './components/system-users-table';

function getErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError<{ message?: string }>;
  return axiosError?.response?.data?.message ?? (error instanceof Error ? error.message : 'An error occurred');
}

export function AccountSystemUsersPage(): React.JSX.Element {
  const { account } = useUser();
  const [showArchived, setShowArchived] = React.useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['system-users', account?.id, showArchived],
    queryFn: async () =>
      await getAllSystemUsers(account!.id, { archived: showArchived }),
    enabled: !!account,
  });

  const users = data?.systemUsers ?? [];

  if (!account) {
    return <div>No account found</div>;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {getErrorMessage(error)}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={showArchived ? 1 : 0}
          onChange={(_, value) => setShowArchived(value === 1)}
        >
          <Tab label="Active Users" />
          <Tab label="Archived Users" />
        </Tabs>
      </Box>
      <SystemUsersTable
        loading={isLoading}
        rows={users}
        showArchived={showArchived}
      />
    </>
  );
}
